/**
 * Authentication settings section.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { SettingCard } from './SettingCard'
import { Toggle } from './Toggle'
import type { AdminSettings } from './types'

const DECIMAL_BASE = 10
const DEFAULT_SESSION_TIMEOUT = 60

interface AuthenticationSectionProps {
  isOrgAdmin: boolean
  settings: AdminSettings
  setSettings: (s: AdminSettings) => void
}

export function AuthenticationSection({
  isOrgAdmin,
  settings,
  setSettings,
}: AuthenticationSectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SettingCard
      title={isOrgAdmin ? t('adminSettings.orgAuthentication') : t('adminSettings.authentication')}
      description={
        isOrgAdmin
          ? t('adminSettings.orgAuthenticationDesc')
          : t('adminSettings.authenticationDesc')
      }
    >
      <div className="space-y-4">
        {!isOrgAdmin && (
          <Toggle
            enabled={settings.allowSelfRegistration}
            onChange={(value) => {
              setSettings({ ...settings, allowSelfRegistration: value })
            }}
            label={t('adminSettings.allowSelfRegistration')}
          />
        )}
        <Toggle
          enabled={settings.requireEmailVerification}
          onChange={(value) => {
            setSettings({ ...settings, requireEmailVerification: value })
          }}
          label={t('adminSettings.requireEmailVerification')}
        />
        <Toggle
          enabled={settings.enableTwoFactor}
          onChange={(value) => {
            setSettings({ ...settings, enableTwoFactor: value })
          }}
          label={t('adminSettings.enableTwoFactor')}
        />
        <div className="border-t border-stone-200 pt-4 dark:border-stone-700">
          <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
            {t('adminSettings.sessionTimeout')}
          </label>
          <input
            type="number"
            min="5"
            max="1440"
            value={settings.sessionTimeoutMinutes}
            onChange={(e) => {
              setSettings({
                ...settings,
                sessionTimeoutMinutes:
                  parseInt(e.target.value, DECIMAL_BASE) || DEFAULT_SESSION_TIMEOUT,
              })
            }}
            className="w-32 rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
          />
        </div>
      </div>
    </SettingCard>
  )
}
